/**
 * Apariencia (icono + color de acento) de cada add-on.
 *
 * Este módulo es el **catálogo** de lo que el panel deja elegir y la traducción
 * de lo que devuelve la API a algo pintable:
 *
 * 1. **`ADDON_ICONOS`** — iconos elegibles. La clave de cada entrada es el slug
 *    que viaja en el campo `icono` del complemento y el que la landing publica
 *    en su `data-testid` (`bolt`, `inventory`, `psychology`, `account-tree`).
 *    Añadir un icono es añadir una entrada aquí.
 * 2. **`ADDON_COLORES`** — paleta cerrada del selector. Los valores salen
 *    **solo** de `src/theme/tokens.ts`: este archivo no escribe ni un hex
 *    (`docs/conventions.md` §3).
 * 3. **`iconoDeAddon` / `claveDeColorAddon`** — la traducción desde el dato de
 *    runtime que manda el backend.
 *
 * ⚠️ **El backend valida forma, no catálogo** (feature 41,
 * `progress/handoff_backend_40.md`): acepta cualquier slug kebab-case de hasta
 * 40 caracteres como `icono` y cualquier `#RRGGBB` como `color`. Este front
 * solo ofrece un catálogo cerrado, pero **tiene que tolerar** valores que no
 * conoce: para eso están `iconoDeAddon` (icono de respaldo estable) y el uso
 * del hex tal cual como color de acento.
 *
 * ⚠️ **No hay estado «sin elección»**: las dos columnas son `NOT NULL` con
 * default, así que todo complemento llega con icono y color. Por eso este
 * módulo ya no persiste nada: la apariencia vive en la API, no en el navegador
 * (la capa de `localStorage` de la feature 40 se eliminó entera).
 */

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BoltIcon from '@mui/icons-material/Bolt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PsychologyIcon from '@mui/icons-material/Psychology';
import type { SvgIconComponent } from '@mui/icons-material';
import { tokens } from '../theme/tokens';

/** Entrada del catálogo de iconos: el componente y cómo se llama en la UI. */
interface AddonIcono {
  readonly Icon: SvgIconComponent;
  /** Etiqueta legible en español; es el nombre accesible del selector. */
  readonly etiqueta: string;
}

/** Entrada del catálogo de colores: el token resuelto y su nombre en la UI. */
interface AddonColor {
  readonly valor: string;
  readonly etiqueta: string;
}

/**
 * Iconos elegibles. **La clave es el slug** que guarda el backend en `icono` y
 * el que la landing publica en `data-testid="addon-${id}-icon-${slug}"`. Son
 * kebab-case y de menos de 40 caracteres, la forma que exige el contrato.
 */
export const ADDON_ICONOS = {
  bolt:           { Icon: BoltIcon,        etiqueta: 'Rayo' },
  inventory:      { Icon: Inventory2Icon,  etiqueta: 'Caja' },
  psychology:     { Icon: PsychologyIcon,  etiqueta: 'Cerebro' },
  'account-tree': { Icon: AccountTreeIcon, etiqueta: 'Flujo' },
} as const satisfies Record<string, AddonIcono>;

export type AddonIconoClave = keyof typeof ADDON_ICONOS;

/**
 * Colores elegibles: la paleta decorativa `tokens.accent` completa más los dos
 * colores de marca que ya se usaban como acento en esta sección
 * (`brand.orange` y `brand.green`). No hay color libre: un selector de color
 * arbitrario metería hex fuera de `src/theme/tokens.ts`, que es justo lo que
 * prohíben `docs/conventions.md` §3 y los tests de `src/theme/__tests__/`.
 *
 * Ojo con la caja: algunos tokens están escritos en minúsculas y el backend
 * devuelve siempre MAYÚSCULAS. Comparar con `claveDeColorAddon`, nunca con `===`.
 */
export const ADDON_COLORES = {
  coral:   { valor: tokens.accent.coral,  etiqueta: 'Coral' },
  violeta: { valor: tokens.accent.violet, etiqueta: 'Violeta' },
  azul:    { valor: tokens.accent.blue,   etiqueta: 'Azul' },
  rojo:    { valor: tokens.accent.red,    etiqueta: 'Rojo' },
  cian:    { valor: tokens.accent.cyan,   etiqueta: 'Cian' },
  ambar:   { valor: tokens.accent.amber,  etiqueta: 'Ámbar' },
  naranja: { valor: tokens.brand.orange,  etiqueta: 'Naranja' },
  verde:   { valor: tokens.brand.green,   etiqueta: 'Verde' },
} as const satisfies Record<string, AddonColor>;

export type AddonColorClave = keyof typeof ADDON_COLORES;

/** Claves en el orden en que se pintan en el panel, sin castear en cada uso. */
export const ADDON_ICONO_CLAVES = Object.keys(ADDON_ICONOS) as AddonIconoClave[];
export const ADDON_COLOR_CLAVES = Object.keys(ADDON_COLORES) as AddonColorClave[];

/**
 * Defaults del backend (`icono TEXT NOT NULL DEFAULT 'bolt'`,
 * `color TEXT NOT NULL DEFAULT '#E8440A'`). Se replican aquí para que el alta
 * del panel preseleccione **exactamente** lo que se guardará si nadie toca los
 * selectores, y para tener un icono de respaldo estable cuando llegue un slug
 * desconocido.
 *
 * `#E8440A` es literalmente `tokens.brand.orange`: el default del backend
 * coincide con el naranja de marca, así que no hace falta escribir el hex aquí
 * (`docs/conventions.md` §3.5). Si algún día dejaran de coincidir, el hex
 * seguiría sin escribirse en el código: se pediría el token que corresponda.
 */
export const ADDON_ICONO_DEFAULT: AddonIconoClave = 'bolt';
export const ADDON_COLOR_DEFAULT: string = tokens.brand.orange;

/**
 * ¿Es `valor` una clave de icono que este front sabe dibujar?
 *
 * Privada a propósito: fuera de aquí lo que hace falta es el icono ya
 * resuelto (`iconoDeAddon`), no el predicado. Si algún día un componente
 * necesitara preguntar por la clave, se exporta entonces.
 */
function esClaveIconoAddon(valor: string): valor is AddonIconoClave {
  return Object.prototype.hasOwnProperty.call(ADDON_ICONOS, valor);
}

/**
 * Icono con el que pintar un complemento, resolviendo el slug que manda la API.
 *
 * El backend acepta slugs que no están en el catálogo (`unicornio-magico` es un
 * caso real de sus tests), así que un valor desconocido **no puede** dejar un
 * hueco ni romper la tarjeta: cae al icono del default del backend, que es
 * estable y siempre existe.
 */
export function iconoDeAddon(icono: string): SvgIconComponent {
  return ADDON_ICONOS[esClaveIconoAddon(icono) ? icono : ADDON_ICONO_DEFAULT].Icon;
}

/**
 * Clave de la paleta que corresponde a un HEX de la API, o `null` si ese color
 * no está en el catálogo del front (el backend admite cualquier `#RRGGBB`).
 *
 * **La comparación es case-insensitive a propósito**: el backend canoniza el
 * color a mayúsculas (`#b90f0f` → `#B90F0F`) y varios tokens están escritos en
 * minúsculas, así que un `===` dejaría el selector sin ningún swatch marcado al
 * abrir un complemento ya guardado.
 */
export function claveDeColorAddon(color: string): AddonColorClave | null {
  const buscado = color.trim().toUpperCase();
  return ADDON_COLOR_CLAVES.find(clave => ADDON_COLORES[clave].valor.toUpperCase() === buscado) ?? null;
}
